import { assumeNonNull } from '@repo/studio-utils/utils/null-check'
import { fromPreloadRouteContext } from '@repo/studio-utils/router-context'
import * as React from 'react'
import { matchPath, RouterContextProvider } from 'react-router'
import type { RouteObject, LoaderFunction, LinkProps } from 'react-router'
import { addDomEvent, requestIdleCallbackPonyfill } from 'utils/dom'

const PreloadRouteContext = React.createContext<RouteObject[] | null>(null)

export function PreloadRouteProvider({
  value,
  children,
}: {
  value: RouteObject[]
  children: React.ReactNode
}) {
  return <PreloadRouteContext value={value}>{children}</PreloadRouteContext>
}

function usePreloadRoutes() {
  return React.useContext(PreloadRouteContext)
}

type LazyFn = () => Promise<unknown> | undefined
const runnersMap = new Map<string, { lazyFns: LazyFn[]; loaderFns: LoaderFunction[] }>()

function findRunners(url: string, currentRoutes: RouteObject[], currentPath: string) {
  const lazyFns: LazyFn[] = []
  const loaderFns: LoaderFunction[] = []

  currentRoutes.forEach(route => {
    const path = (currentPath + '/' + (route.path || '/')).replace('//', '/').replace(/\/$/, '')

    const is404 = path === '/*'
    const isReferral = path.includes('/ref/')

    if (is404 || isReferral) {
      return
    }

    const match = !is404 ? matchPath({ path, end: true, caseSensitive: false }, url) : null

    if (match) {
      const lazyFn = typeof route.lazy === 'function' ? route.lazy : null
      const params = match.params

      if (lazyFn) {
        lazyFns.push(() => lazyFn())
      }

      if (route.loader && typeof route.loader === 'function') {
        const loader = route.loader
        loaderFns.push(args => loader({ ...args, params, pattern: path }))
      } else if (lazyFn) {
        loaderFns.push(args =>
          lazyFn()?.then(module => {
            if (
              typeof module === 'object' &&
              module !== null &&
              'loader' in module &&
              typeof module.loader === 'function'
            ) {
              return module.loader({ ...args, params, pattern: path })
            }
          }),
        )
      }
    }

    if (route.children) {
      const next = findRunners(url, route.children, path)
      lazyFns.push(...next.lazyFns)
      loaderFns.push(...next.loaderFns)
    }
  })

  return { lazyFns, loaderFns }
}

export function findRoutePreloaderByUrl(routes: RouteObject[], url: string) {
  if (!runnersMap.has(url)) {
    const runners = findRunners(url, routes, '')
    runnersMap.set(url, runners)
  }

  return runnersMap.get(url)
}

export function preloadRouteLazy(lazyFns?: LazyFn[]) {
  lazyFns?.forEach(lazy => lazy())
}

let queue: (() => void)[] | null = []
let init = false

export function usePreloadRoute({
  to,
  enabled = true,
}: {
  to: LinkProps['to']
  enabled?: boolean
}) {
  const routes = usePreloadRoutes()

  return {
    preloadRoute: React.useCallback(() => {
      if (!enabled || !routes) {
        return
      }

      if (process.env.TEST_BROWSER) {
        return
      }

      if (!init) {
        const unsubscribe = addDomEvent(window, 'click', () => {
          queue?.forEach(runner => runner())
          queue = null
          unsubscribe()
        })
        init = true
      }

      const url = typeof to === 'string' ? to : to.pathname || ''

      const { lazyFns, loaderFns } = assumeNonNull(findRoutePreloaderByUrl(routes, url))

      const runner = () => {
        requestIdleCallbackPonyfill(() => {
          const preloadUrl = new URL(url, window.location.href)
          const context = new RouterContextProvider()
          context.set(fromPreloadRouteContext, true)

          lazyFns?.forEach(lazy => lazy())
          loaderFns?.forEach(loader => {
            // `params` and `pattern` are placeholders: each entry in `loaderFns`
            // closes over the route it matched and overrides both with the real
            // values before handing them to the loader.
            loader({
              params: {},
              pattern: '',
              request: new Request(preloadUrl),
              url: preloadUrl,
              context,
            })
          })
        })
      }

      if (queue) {
        queue?.push(runner)
      } else {
        runner()
      }

      return {
        lazyFns,
        loaderFns,
      }
    }, [enabled, to, routes]),
  }
}
